/*
 * AAIKO FAQ accordion behaviour. One custom element, guarded so repeated
 * sections on a page never redefine it.
 *
 *   aaiko-faq-accordion   category tabs + question search over <details> rows
 *
 * Opening and closing a row is native <details>; nothing here touches it.
 * What this file does is filtering: which rows the active tab and the search
 * box leave visible, and which of those is last (mobile draws no border on the
 * final row, so that has to follow the visible list, not the source order).
 *
 * Search is scoped to the active category. Searching across every tab would
 * leave the underlined tab pointing at a category the results are not in,
 * and the frames draw no state for that.
 *
 * Nothing here is drawn: the Figma frames carry one closed, unfiltered state.
 */
(function () {
  'use strict';

  function normalise(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function handleize(value) {
    return normalise(value).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  if (customElements.get('aaiko-faq-accordion')) return;

  customElements.define(
    'aaiko-faq-accordion',
    class AaikoFaqAccordion extends HTMLElement {
      connectedCallback() {
        this.rows = Array.prototype.slice.call(this.querySelectorAll('[data-aaiko-faq-row]'));
        this.panel = this.querySelector('[data-aaiko-faq-list]');
        this.buildTabs();
        this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-aaiko-faq-tab]'));
        this.empty = this.querySelector('[data-aaiko-faq-empty]');
        this.search = this.querySelector('[data-aaiko-faq-search]');
        this.category = this.tabs.length ? this.tabs[0].getAttribute('data-aaiko-faq-tab') : '';

        this.tabs.forEach(
          function (tab, index) {
            tab.addEventListener(
              'click',
              function () {
                this.select(index, false);
              }.bind(this)
            );
            tab.addEventListener('keydown', this.onTabKeydown.bind(this, index));
          }.bind(this)
        );

        if (this.search) {
          this.search.addEventListener('input', this.apply.bind(this));
        }

        /* The strip is a scroller at 420 (see the section's stylesheet), and a
           scroller nobody can see is the same as no scroller: AAIKO's report was
           "de laatste categorie valt van het scherm" followed by "is die
           swipe-able?", which is one person looking at a row that ends in a
           clean edge and concluding it is finished.

           So the strip says which way it can still travel, and the stylesheet
           fades that edge out. Attributes rather than inline styles, so the
           whole appearance stays in one place; passive listener, because this
           only ever reads geometry. */
        this.strip = this.querySelector('[data-aaiko-faq-tabs]');
        if (this.strip) {
          this.syncStripEdges = this.syncStripEdges.bind(this);
          this.strip.addEventListener('scroll', this.syncStripEdges, { passive: true });
          window.addEventListener('resize', this.syncStripEdges);
          this.syncStripEdges();
        }

        this.apply();

        this.onHashChange = this.openFromHash.bind(this, true);
        window.addEventListener('hashchange', this.onHashChange);
        this.openFromHash(false);
      }

      disconnectedCallback() {
        if (this.syncStripEdges) window.removeEventListener('resize', this.syncStripEdges);
        if (this.onHashChange) window.removeEventListener('hashchange', this.onHashChange);
      }

      /* REQUEST (Michael, 16/10/2026): the cart USPs link here as
         `#faq-<category>` (snippets/aaiko-usp-list), so "Eenvoudig retourneren"
         lands on the Retourneren tab. The slug is compared handleized on both
         sides, the same transform Liquid's `handleize` applies to the locale
         string. No matching tab leaves the strip as it was; the visitor is
         still on the FAQ. Scrolls to the section either way, because the hash
         names no element and the browser will not. */
      openFromHash(smooth) {
        var hash = decodeURIComponent((window.location.hash || '').slice(1));
        if (hash.indexOf('faq-') !== 0) return;
        var slug = hash.slice(4);

        for (var i = 0; i < this.tabs.length; i++) {
          if (handleize(this.tabs[i].getAttribute('data-aaiko-faq-tab')) === slug) {
            this.select(i, false);
            /* At 420 the strip scrolls: bring the chosen tab in from the
               left inset. Horizontal only, so the page itself does not move
               here. */
            if (this.strip) {
              var left = this.tabs[i].getBoundingClientRect().left - this.strip.getBoundingClientRect().left;
              this.strip.scrollLeft += left - parseFloat(getComputedStyle(this.strip).paddingLeft || 0);
              this.syncStripEdges();
            }
            break;
          }
        }

        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ block: 'start', behavior: smooth && !reduce ? 'smooth' : 'auto' });
      }

      /* 1px of slack: scrollLeft and the widths are fractional at most zoom
         levels, so an exact comparison leaves a permanent fade on a strip that
         is already at its end. */
      syncStripEdges() {
        var strip = this.strip;
        if (!strip) return;
        var max = strip.scrollWidth - strip.clientWidth;
        strip.toggleAttribute('data-aaiko-faq-more-start', strip.scrollLeft > 1);
        strip.toggleAttribute('data-aaiko-faq-more-end', strip.scrollLeft < max - 1);
      }

      /*
       * A section cannot read a theme block's settings, so when the rows come
       * from aaiko-faq-item blocks rather than the faq_item metaobject the
       * Liquid side has no category list and renders the strip empty. Fill it
       * from the rows, which DO carry their category. The drawn markup is
       * reproduced exactly, so a JS-built tab is indistinguishable from a
       * Liquid-built one.
       */
      buildTabs() {
        var strip = this.querySelector('[data-aaiko-faq-tabs]');
        if (!strip || strip.querySelector('[data-aaiko-faq-tab]')) return;

        var seen = [];
        this.rows.forEach(function (row) {
          var cat = row.getAttribute('data-aaiko-faq-category');
          if (cat && seen.indexOf(cat) === -1) seen.push(cat);
        });
        if (!seen.length) {
          strip.remove();
          return;
        }

        var panelId = this.panel ? this.panel.id : '';
        var base = (this.id || panelId || 'aaiko-faq') + '-tab-';
        seen.forEach(function (cat, index) {
          var tab = document.createElement('button');
          tab.type = 'button';
          tab.setAttribute('role', 'tab');
          tab.className = 'aaiko-faq__tab';
          tab.id = base + (index + 1);
          if (panelId) tab.setAttribute('aria-controls', panelId);
          tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
          tab.setAttribute('tabindex', index === 0 ? '0' : '-1');
          tab.setAttribute('data-aaiko-faq-tab', cat);

          var label = document.createElement('span');
          label.className = 'aaiko-faq__tablabel';
          label.textContent = cat;
          tab.appendChild(label);
          strip.appendChild(tab);
        });

        if (this.panel) {
          this.panel.setAttribute('role', 'tabpanel');
          this.panel.setAttribute('aria-labelledby', base + '1');
        }
      }

      /* Roving tabindex: one stop for the whole list, arrows move within it. */
      onTabKeydown(index, event) {
        var last = this.tabs.length - 1;
        var next = null;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;

        if (next === null) return;
        event.preventDefault();
        this.select(next, true);
      }

      select(index, focus) {
        var chosen = this.tabs[index];
        if (!chosen) return;

        this.category = chosen.getAttribute('data-aaiko-faq-tab');
        this.tabs.forEach(function (tab) {
          var on = tab === chosen;
          tab.setAttribute('aria-selected', String(on));
          tab.setAttribute('tabindex', on ? '0' : '-1');
        });
        if (this.panel && chosen.id) this.panel.setAttribute('aria-labelledby', chosen.id);
        if (focus) chosen.focus();

        this.apply();
      }

      apply() {
        /* CLIENT REPORT (feedback 09/09/2026): "de zoekfunctie onder
           Veelgestelde vragen werkt nog niet."

           It worked, but only inside the tab you happened to be standing on:
           the two filters were ANDed, and the strip always opens on the first
           category. So typing "retour" while Bestellingen was selected - which
           is every first search on the page - matched nothing and read as a
           dead field.

           A query now searches the WHOLE list and the category stands down for
           as long as there is one. That is the only reading that makes sense of
           a search box sitting ABOVE the tabs: it is a way past them, not a
           filter within one. The strip drops its selected underline while a
           query is live, so the row of categories cannot claim to be filtering
           something it is not, and clearing the field hands it straight back. */
        var query = normalise(this.search ? this.search.value : '');
        var category = query ? '' : this.category;
        var visible = [];

        var current = this.category;
        this.tabs.forEach(function (tab) {
          tab.setAttribute('aria-selected', String(!query && tab.getAttribute('data-aaiko-faq-tab') === current));
        });

        this.rows.forEach(function (row) {
          var inCategory = !category || row.getAttribute('data-aaiko-faq-category') === category;
          var matches = true;

          if (query) {
            var label = row.querySelector('.aaiko-faq__q');
            matches = normalise(label ? label.textContent : '').indexOf(query) !== -1;
          }

          var show = inCategory && matches;
          row.hidden = !show;
          row.removeAttribute('data-aaiko-faq-last');
          /* A hidden row left open would spring back open on the next filter. */
          if (!show) row.open = false;
          if (show) visible.push(row);
        });

        if (visible.length) visible[visible.length - 1].setAttribute('data-aaiko-faq-last', '');
        if (this.empty) this.empty.hidden = visible.length > 0;
      }
    }
  );
})();
